/**
 * WhatsApp Link Component
 * Renders a clickable WhatsApp link with automatic number formatting
 */

import React from 'react';
import { MessageCircle } from 'lucide-react';

export interface WhatsAppLinkProps {
  /** Phone number (formatted or not) - ex: "5511999998888" or "(11) 99999-8888" */
  phone: string | null | undefined;

  /** Custom label for the link */
  label?: string;

  /** Show only icon without text */
  iconOnly?: boolean;

  /** Pre-filled message (optional) */
  message?: string;

  /** Additional CSS classes */
  className?: string;

  /** Icon size */
  iconSize?: 'sm' | 'md' | 'lg';

  /** Render as button instead of link (to avoid nested <a> tags) */
  asButton?: boolean;

  /** Callback when clicked (for analytics) */
  onClick?: () => void;
}

/**
 * Format phone number for display
 * Ex: 5511999998888 → +55 11 99999-8888
 */
function formatPhoneDisplay(phone: string): string {
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Try to match pattern: country(2) area(2) prefix(4-5) suffix(4)
  const match = cleaned.match(/^(\d{2})(\d{2})(\d{4,5})(\d{4})$/);

  if (!match) {
    return phone; // Return original if can't format
  }

  const [, country, area, prefix, suffix] = match;
  return `+${country} ${area} ${prefix}-${suffix}`;
}

/**
 * WhatsApp Link Component
 */
export function WhatsAppLink({
  phone,
  label,
  iconOnly = false,
  message,
  className = '',
  iconSize = 'md',
  asButton = false,
  onClick
}: WhatsAppLinkProps) {
  // Validation: return null if no phone
  if (!phone) {
    return null;
  }

  // Clean and format phone number
  let cleanPhone = phone.trim();

  // Se começa com +, é formato internacional - validar e limpar
  if (cleanPhone.startsWith('+')) {
    cleanPhone = cleanPhone.replace(/[^\d+]/g, '');
    // Validar mínimo de 8 dígitos após o +
    if (cleanPhone.length < 9) {
      return null;
    }
    // Remover o + para o wa.me (wa.me aceita sem +)
    const formattedPhone = cleanPhone.substring(1);

    // Validar se tem pelo menos 10 dígitos
    if (formattedPhone.length < 10) {
      return null;
    }

    // Retornar early para números internacionais
    const params = new URLSearchParams();
    if (message) {
      params.set('text', message);
    }
    const url = `https://wa.me/${formattedPhone}${
      params.toString() ? '?' + params.toString() : ''
    }`;

    const displayLabel = label || cleanPhone;
    const iconSizeClass = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6'
    }[iconSize];

    const content = (
      <>
        <MessageCircle className={iconSizeClass} />
        {!iconOnly && <span>{displayLabel}</span>}
      </>
    );

    const commonClasses = `inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors ${className}`;

    if (asButton) {
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
            window.open(url, '_blank', 'noopener,noreferrer');
          }}
          className={commonClasses}
          title="Enviar mensagem no WhatsApp"
        >
          {content}
        </button>
      );
    }

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={commonClasses}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        title="Enviar mensagem no WhatsApp"
      >
        {content}
      </a>
    );
  }

  // Número sem código internacional - assumir Brasil
  cleanPhone = phone.replace(/\D/g, '');

  // Validate minimum length
  if (!cleanPhone || cleanPhone.length < 10) {
    return null;
  }

  // Ensure country code (default to Brazil: 55)
  const formattedPhone = cleanPhone.startsWith('55')
    ? cleanPhone
    : `55${cleanPhone}`;

  // Build WhatsApp URL
  const params = new URLSearchParams();
  if (message) {
    params.set('text', message);
  }

  const url = `https://wa.me/${formattedPhone}${
    params.toString() ? '?' + params.toString() : ''
  }`;

  // Display label (formatted or custom)
  const displayLabel = label || formatPhoneDisplay(formattedPhone);

  // Icon size classes
  const iconSizeClass = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  }[iconSize];

  // Shared classes
  const sharedClasses = `
    inline-flex items-center gap-2
    text-primary hover:text-primary/80
    transition-colors duration-200
    ${className}
  `;

  // Shared content
  const content = (
    <>
      <MessageCircle className={iconSizeClass} />
      {!iconOnly && (
        <span className="text-sm font-medium">
          {displayLabel}
        </span>
      )}
    </>
  );

  // Render as button to avoid nested <a> tags
  if (asButton) {
    const handleButtonClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClick?.();
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
      <button
        onClick={handleButtonClick}
        className={sharedClasses}
        title={`Abrir conversa no WhatsApp${displayLabel ? ` com ${displayLabel}` : ''}`}
        type="button"
      >
        {content}
      </button>
    );
  }

  // Render as link (default)
  const handleLinkClick = () => {
    onClick?.();
    // Let browser handle the link normally
  };

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleLinkClick}
      className={sharedClasses}
      title={`Abrir conversa no WhatsApp${displayLabel ? ` com ${displayLabel}` : ''}`}
    >
      {content}
    </a>
  );
}

/**
 * WhatsApp Button Variant
 * Renders as a button instead of a link
 */
export function WhatsAppButton({
  phone,
  label = 'WhatsApp',
  message,
  className = '',
  onClick
}: Pick<WhatsAppLinkProps, 'phone' | 'label' | 'message' | 'className' | 'onClick'>) {
  if (!phone) return null;

  const cleanPhone = phone.replace(/\D/g, '');
  if (!cleanPhone || cleanPhone.length < 10) return null;

  const formattedPhone = cleanPhone.startsWith('55')
    ? cleanPhone
    : `55${cleanPhone}`;

  const params = new URLSearchParams();
  if (message) params.set('text', message);

  const url = `https://wa.me/${formattedPhone}${
    params.toString() ? '?' + params.toString() : ''
  }`;

  const handleClick = () => {
    onClick?.();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <button
      onClick={handleClick}
      className={`
        inline-flex items-center gap-2
        px-4 py-2 rounded-lg
        bg-primary hover:bg-primary/90
        text-primary-foreground font-medium text-sm
        transition-colors duration-200
        ${className}
      `}
    >
      <MessageCircle className="h-4 w-4" />
      {label}
    </button>
  );
}

/**
 * WhatsApp Badge Variant
 * Small badge with icon and optional link
 */
export function WhatsAppBadge({
  phone,
  message,
  showPhone = false
}: Pick<WhatsAppLinkProps, 'phone' | 'message'> & { showPhone?: boolean }) {
  if (!phone) return null;

  return (
    <div className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-xs">
      <MessageCircle className="h-3 w-3" />
      {showPhone ? (
        <WhatsAppLink
          phone={phone}
          message={message}
          iconOnly={false}
          className="text-primary hover:text-primary/80"
          iconSize="sm"
        />
      ) : (
        <span>WhatsApp</span>
      )}
    </div>
  );
}
